import { useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, Trash2, RefreshCw, ShieldAlert } from 'lucide-react';

const VARIANT_CONFIG = {
  danger: {
    icon: AlertCircle,
    iconClass: 'text-destructive bg-destructive/10',
    actionClass: 'bg-destructive hover:bg-destructive/90 text-white',
  },
  delete: {
    icon: Trash2,
    iconClass: 'text-destructive bg-destructive/10',
    actionClass: 'bg-destructive hover:bg-destructive/90 text-white',
  },
  warning: {
    icon: ShieldAlert,
    iconClass: 'text-warning bg-warning/10',
    actionClass: 'bg-warning hover:bg-warning/90 text-white',
  },
  default: {
    icon: AlertCircle,
    iconClass: 'text-primary bg-primary/10',
    actionClass: '',
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  loading = false,
}) {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.default;
  const Icon = config.icon;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config.iconClass}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <AlertDialogTitle>{title}</AlertDialogTitle>
              <AlertDialogDescription className="mt-1.5">
                {description}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading} data-testid="confirm-dialog-cancel">
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={loading}
            className={config.actionClass}
            data-testid="confirm-dialog-action"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Hook for imperative confirmation dialogs.
 * Returns [ConfirmDialogElement, confirm(options)] 
 * Usage:
 *   const [ConfirmEl, confirm] = useConfirm();
 *   // In handler:
 *   const ok = await confirm({ title: '...', description: '...' });
 *   if (!ok) return;
 *   // ... proceed
 *   // In JSX: {ConfirmEl}
 */
export function useConfirm() {
  const [state, setState] = useState({ open: false, title: '', description: '', confirmLabel: 'Confirm', variant: 'default', resolve: null });

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || 'Are you sure?',
        description: opts.description || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        variant: opts.variant || 'default',
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState(s => ({ ...s, open: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state.resolve is the intended dep
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState(s => ({ ...s, open: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state.resolve is the intended dep
  }, [state.resolve]);

  const element = (
    <ConfirmDialog
      open={state.open}
      onOpenChange={(open) => { if (!open) handleCancel(); }}
      title={state.title}
      description={state.description}
      confirmLabel={state.confirmLabel}
      variant={state.variant}
      onConfirm={handleConfirm}
    />
  );

  return [element, confirm];
}

export default ConfirmDialog;
